import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  ArrowLeft, Plus, Edit2, Trash2, X, Upload, 
  Search, Package as PackageIcon, FileText, Car
} from 'lucide-react';
import { curationService } from '../services/curationService';
import { cloudinaryService } from '../services/cloudinaryService';
import SequenceManager from '../components/SequenceManager';

const CurationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Dialog states
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isFAQDialogOpen, setIsFAQDialogOpen] = useState(false);
  const [isSequenceDialogOpen, setIsSequenceDialogOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageToDelete, setPackageToDelete] = useState(null);
  const [activeTab, setActiveTab] = useState('package-info');
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    complete_package_price: 0,
    package_image_url: '',
    vehicles: [],
    brochure_url: '',
    is_active: true
  });
  
  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [faqInput, setFaqInput] = useState({ question: '', answer: '' });
  
  // Vehicle management
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehicleFormData, setVehicleFormData] = useState({
    name: '',
    description: '',
    segment: 'Commuter',
    usage_days: 0,
    shared_owners: 0,
    specifications: [],
    images: []
  });
  const [vehicleSpecInput, setVehicleSpecInput] = useState('');
  const [uploadingVehicleImages, setUploadingVehicleImages] = useState(false);
  
  // Image/brochure states
  const [uploadingPackageImage, setUploadingPackageImage] = useState(false);
  const [uploadingBrochure, setUploadingBrochure] = useState(false);
  const [imagesToDelete, setImagesToDelete] = useState([]);
  const [brochureToDelete, setBrochureToDelete] = useState(null);
  const [pendingBrochureFile, setPendingBrochureFile] = useState(null);
  
  // Cloudinary config
  const CLOUDINARY_CLOUD_NAME = 'dkallcbko';
  const CLOUDINARY_UPLOAD_PRESET = 'luxury_assets_unsigned';
  const CLOUDINARY_BASE_URL = `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}`;

  useEffect(() => {
    fetchPackages();
    fetchFAQs();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = packages.filter(pkg =>
        pkg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pkg.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPackages(filtered);
    } else {
      setFilteredPackages(packages);
    }
  }, [searchTerm, packages]);

  const fetchPackages = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await curationService.getAllPackages();
      console.log('Fetched packages:', data);
      setPackages(data);
      setFilteredPackages(data);
    } catch (err) {
      console.error('Error fetching packages:', err);
      setError(err.response?.data?.detail || 'Failed to load packages');
    } finally {
      setLoading(false);
    }
  };

  const fetchFAQs = async () => {
    try {
      const data = await curationService.getFAQs();
      console.log('Fetched FAQs:', data);
      setFaqs(data.faqs || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
    }
  };

  const showSuccess = (message) => {
    setSuccess(typeof message === 'string' ? message : JSON.stringify(message));
    setTimeout(() => setSuccess(''), 3000);
  };

  const showError = (message) => {
    // Handle different error formats
    let errorMsg = '';
    if (typeof message === 'string') {
      errorMsg = message;
    } else if (message?.message) {
      errorMsg = message.message;
    } else if (Array.isArray(message)) {
      errorMsg = message.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
    } else {
      errorMsg = JSON.stringify(message);
    }
    setError(errorMsg);
    setTimeout(() => setError(''), 5000);
  };

  // ========== PACKAGE CRUD OPERATIONS ==========
  
  const handleOpenDialog = (pkg = null) => {
    if (pkg) {
      setEditingPackage(pkg);
      setFormData({
        name: pkg.name || '',
        description: pkg.description || '',
        complete_package_price: pkg.complete_package_price || 0,
        package_image_url: pkg.package_image_url || '',
        vehicles: pkg.vehicles || [],
        brochure_url: pkg.brochure_url || '',
        is_active: pkg.is_active !== false
      });
    } else {
      setEditingPackage(null);
      setFormData({
        name: '',
        description: '',
        complete_package_price: 0,
        package_image_url: '',
        vehicles: [],
        brochure_url: '',
        is_active: true
      });
    }
    setImagesToDelete([]);
    setBrochureToDelete(null);
    setActiveTab('package-info');
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingPackage(null);
    setImagesToDelete([]);
    setBrochureToDelete(null);
    setPendingBrochureFile(null); // Clear pending brochure file
    setActiveTab('package-info');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.name.trim()) {
      showError('Package name is required');
      return;
    }
    if (!formData.description.trim()) {
      showError('Description is required');
      return;
    }
    if (!formData.complete_package_price || formData.complete_package_price <= 0) {
      showError('Complete package price is required and must be greater than 0');
      return;
    }

    try {
      // Handle deferred image deletions
      if (imagesToDelete.length > 0) {
        console.log('Deleting images:', imagesToDelete);
        for (const imageUrl of imagesToDelete) {
          try {
            await curationService.deleteImage(imageUrl);
          } catch (err) {
            console.error('Failed to delete image:', imageUrl, err);
          }
        }
      }

      // Handle brochure deletion
      if (brochureToDelete) {
        console.log('Deleting brochure:', brochureToDelete);
        try {
          await curationService.deleteImage(brochureToDelete);
        } catch (err) {
          console.error('Failed to delete brochure:', brochureToDelete, err);
        }
      }

      // Upload pending brochure file if exists
      let finalBrochureUrl = formData.brochure_url;
      if (pendingBrochureFile) {
        console.log('📤 Uploading pending brochure file to Cloudinary...');
        setUploadingBrochure(true);
        try {
          const result = await cloudinaryService.uploadPdf(pendingBrochureFile, 'curation');
          finalBrochureUrl = result.secure_url;
          console.log('✅ Brochure uploaded successfully:', result.secure_url);
          setPendingBrochureFile(null); // Clear pending file
        } catch (uploadError) {
          console.error('❌ Error uploading brochure:', uploadError);
          setError(`Failed to upload brochure: ${uploadError.message}`);
          setUploadingBrochure(false);
          return; // Stop submission if brochure upload fails
        } finally {
          setUploadingBrochure(false);
        }
      }

      // Prepare data for backend (add dummy values for backend-required fields)
      const backendData = {
        ...formData,
        brochure_url: finalBrochureUrl, // Use the final brochure URL (uploaded or existing)
        // Backend still requires these fields even though we don't use them
        car_model: 'N/A',
        car_brand: 'N/A',
        duration_days: 360,
        price_per_day: Math.floor(formData.complete_package_price / 360) || 0,
        currency: 'INR'
      };

      console.log('Submitting package data:', backendData);

      // Save package
      if (editingPackage) {
        await curationService.updatePackage(editingPackage.id, backendData);
        showSuccess('Package updated successfully!');
      } else {
        await curationService.createPackage(backendData);
        showSuccess('Package created successfully!');
      }
      
      await fetchPackages();
      setImagesToDelete([]); // Clear deletion queue after successful save
      setBrochureToDelete(null); // Clear brochure deletion
      setPendingBrochureFile(null); // Clear pending brochure file
      handleCloseDialog();
    } catch (err) {
      console.error('Error saving package:', err);
      console.error('Error response:', err.response);
      
      // Handle different error formats from backend
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation errors
          const errors = detail.map(e => {
            const field = e.loc ? e.loc.join('.') : 'field';
            const msg = e.msg || e.message || 'Invalid value';
            return `${field}: ${msg}`;
          }).join(', ');
          showError(errors);
        } else {
          showError(detail);
        }
      } else if (err.message) {
        showError(err.message);
      } else {
        showError('Failed to save package. Please check all required fields.');
      }
    }
  };

  const handleDeleteClick = (pkg) => {
    setPackageToDelete(pkg);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!packageToDelete) return;
    
    setError('');
    try {
      // Delete all package images from Cloudinary using consolidated method
      try {
        console.log('🗑️ Deleting all Cloudinary images for package:', packageToDelete.name);
        await curationService.deletePackageImages(packageToDelete);
        console.log('✅ Cloudinary cleanup completed');
      } catch (err) {
        console.error('❌ Failed to clean up Cloudinary images:', err);
        // Continue with package deletion even if image cleanup fails
      }

      // Delete package from database
      await curationService.deletePackage(packageToDelete.id);
      showSuccess('Package deleted successfully!');
      await fetchPackages();
      setIsDeleteDialogOpen(false);
      setPackageToDelete(null);
    } catch (err) {
      console.error('Error deleting package:', err);
      showError(err.response?.data?.detail || 'Failed to delete package');
      setIsDeleteDialogOpen(false);
      setPackageToDelete(null);
    }
  };

  const toggleActive = async (pkg) => {
    setError('');
    try {
      await curationService.updatePackage(pkg.id, { ...pkg, is_active: !pkg.is_active });
      showSuccess(`Package ${!pkg.is_active ? 'activated' : 'deactivated'} successfully!`);
      await fetchPackages();
    } catch (err) {
      console.error('Error updating package:', err);
      showError(err.response?.data?.detail || 'Failed to update package');
    }
  };

  // ========== SEQUENCE MANAGEMENT ==========
  
  const handleUpdateSequences = async (sequences) => {
    setError('');
    try {
      await curationService.updatePackageSequences(sequences);
      showSuccess('Package display sequence updated successfully!');
      await fetchPackages();
    } catch (err) {
      console.error('Error updating sequences:', err);
      showError(err.response?.data?.detail || 'Failed to update package sequences');
      throw err; // Re-throw to handle in SequenceManager
    }
  };

  // ========== IMAGE MANAGEMENT (PACKAGE) ==========
  
  const uploadToCloudinary = async (file, folder) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
    formData.append('folder', folder);

    const response = await fetch(`${CLOUDINARY_BASE_URL}/image/upload`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload image to Cloudinary');
    }

    const data = await response.json();
    return data.secure_url;
  };

  const handlePackageImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingPackageImage(true);
    setError('');

    try {
      const packageName = formData.name || 'Untitled Package';
      const folder = `The Haute Circle/Curation Packages/${packageName}`;
      
      const url = await uploadToCloudinary(file, folder);
      
      // Mark old image for deletion if exists
      if (formData.package_image_url) {
        setImagesToDelete([...imagesToDelete, formData.package_image_url]);
      }

      setFormData({
        ...formData,
        package_image_url: url
      });
      showSuccess('Package image uploaded successfully!');
    } catch (err) {
      console.error('Error uploading package image:', err);
      showError('Failed to upload package image');
    } finally {
      setUploadingPackageImage(false);
      e.target.value = '';
    }
  };

  const handleRemovePackageImage = () => {
    if (formData.package_image_url) {
      setImagesToDelete([...imagesToDelete, formData.package_image_url]);
      setFormData({
        ...formData,
        package_image_url: ''
      });
    }
  };

  // ========== BROCHURE MANAGEMENT ==========
  
  const handleBrochureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (file.type !== 'application/pdf') {
      showError('Only PDF files are allowed for brochures');
      e.target.value = '';
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    if (file.size > maxSize) {
      showError(`Brochure size must be less than 10MB (current: ${fileSizeMB}MB)`);
      e.target.value = '';
      return;
    }

    console.log(`📄 Brochure file selected: ${file.name} (${fileSizeMB} MB) - Will upload on save`);

    // Store file for upload on save, don't upload immediately
    setPendingBrochureFile(file);
    
    // Mark old brochure for deletion if exists
    if (formData.brochure_url && formData.brochure_url.includes('cloudinary.com')) {
      setBrochureToDelete(formData.brochure_url);
    }

    // Update UI to show pending upload
    setFormData({
      ...formData,
      brochure_url: `pending:${file.name}` // Temporary indicator
    });

    console.log(`✅ Brochure file ready for upload: ${file.name} (${fileSizeMB} MB)`);
  };

  const handleRemoveBrochure = () => {
    if (formData.brochure_url && formData.brochure_url.includes('cloudinary.com')) {
      setBrochureToDelete(formData.brochure_url);
    }
    setPendingBrochureFile(null); // Clear any pending file
    setFormData({
      ...formData,
      brochure_url: ''
    });
  };

  // ========== VEHICLE MANAGEMENT ==========
  
  const handleOpenVehicleDialog = (vehicle = null, index = null) => {
    if (vehicle) {
      setEditingVehicle({ ...vehicle, index });
      setVehicleFormData({
        name: vehicle.name || '',
        description: vehicle.description || '',
        segment: vehicle.segment || 'Commuter',
        usage_days: vehicle.usage_days || 0,
        shared_owners: vehicle.shared_owners || 0,
        specifications: vehicle.specifications || [],
        images: vehicle.images || []
      });
    } else {
      setEditingVehicle(null);
      setVehicleFormData({
        name: '',
        description: '',
        segment: 'Commuter',
        usage_days: 0,
        shared_owners: 0,
        specifications: [],
        images: []
      });
    }
    setVehicleSpecInput('');
    setVehicleDialogOpen(true);
  };

  const handleCloseVehicleDialog = () => {
    setVehicleDialogOpen(false);
    setEditingVehicle(null);
    setVehicleSpecInput('');
  };

  const handleVehicleSubmit = (e) => {
    e.preventDefault();
    
    if (!vehicleFormData.name.trim()) {
      showError('Vehicle name is required');
      return;
    }

    if (editingVehicle !== null && editingVehicle.index !== undefined) {
      // Update existing vehicle
      const updatedVehicles = [...formData.vehicles];
      updatedVehicles[editingVehicle.index] = vehicleFormData;
      setFormData({
        ...formData,
        vehicles: updatedVehicles
      });
      showSuccess('Vehicle updated!');
    } else {
      // Add new vehicle
      setFormData({
        ...formData,
        vehicles: [...formData.vehicles, vehicleFormData]
      });
      showSuccess('Vehicle added!');
    }

    handleCloseVehicleDialog();
  };

  const handleRemoveVehicle = (index) => {
    const vehicle = formData.vehicles[index];
    
    // Mark vehicle images for deletion
    if (vehicle.images?.length > 0) {
      setImagesToDelete([...imagesToDelete, ...vehicle.images]);
    }

    setFormData({
      ...formData,
      vehicles: formData.vehicles.filter((_, i) => i !== index)
    });
    showSuccess('Vehicle removed!');
  };

  const handleAddVehicleSpec = () => {
    if (vehicleSpecInput.trim()) {
      setVehicleFormData({
        ...vehicleFormData,
        specifications: [...vehicleFormData.specifications, vehicleSpecInput.trim()]
      });
      setVehicleSpecInput('');
    }
  };

  const handleRemoveVehicleSpec = (index) => {
    setVehicleFormData({
      ...vehicleFormData,
      specifications: vehicleFormData.specifications.filter((_, i) => i !== index)
    });
  };

  const handleVehicleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingVehicleImages(true);
    setError('');

    try {
      const packageName = formData.name || 'Untitled Package';
      const vehicleName = vehicleFormData.name || 'Untitled Vehicle';
      const folder = `The Haute Circle/Curation Packages/${packageName}/vehicles/${vehicleName}`;
      
      const uploadedUrls = [];
      for (const file of files) {
        try {
          const url = await uploadToCloudinary(file, folder);
          uploadedUrls.push(url);
          console.log('Uploaded vehicle image:', url);
        } catch (err) {
          console.error('Failed to upload vehicle image:', file.name, err);
          showError(`Failed to upload ${file.name}`);
        }
      }

      if (uploadedUrls.length > 0) {
        setVehicleFormData({
          ...vehicleFormData,
          images: [...vehicleFormData.images, ...uploadedUrls]
        });
        showSuccess(`${uploadedUrls.length} vehicle image(s) uploaded!`);
      }
    } catch (err) {
      console.error('Error uploading vehicle images:', err);
      showError('Failed to upload vehicle images');
    } finally {
      setUploadingVehicleImages(false);
      e.target.value = '';
    }
  };

  const handleRemoveVehicleImage = (imageUrl) => {
    setVehicleFormData({
      ...vehicleFormData,
      images: vehicleFormData.images.filter(url => url !== imageUrl)
    });
    setImagesToDelete([...imagesToDelete, imageUrl]);
  };

  // ========== FAQ MANAGEMENT ==========
  
  const handleOpenFAQDialog = () => {
    setIsFAQDialogOpen(true);
  };

  const handleAddFAQ = () => {
    if (faqInput.question.trim() && faqInput.answer.trim()) {
      setFaqs([...faqs, { ...faqInput, id: Date.now().toString() }]);
      setFaqInput({ question: '', answer: '' });
    }
  };

  const handleRemoveFAQ = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const handleSaveFAQs = async () => {
    try {
      await curationService.updateFAQs({ faqs });
      showSuccess('FAQs updated successfully!');
      setIsFAQDialogOpen(false);
    } catch (err) {
      console.error('Error updating FAQs:', err);
      showError('Failed to update FAQs');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={16} />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Curation Packages</h1>
              <p className="text-sm text-gray-600 mt-1">Manage 360-day luxury curation packages</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setIsSequenceDialogOpen(true)}
              className="flex items-center gap-2"
              disabled={packages.length === 0}
            >
              📋 Manage Sequence
            </Button>
            <Button
              variant="outline"
              onClick={handleOpenFAQDialog}
              className="flex items-center gap-2"
            >
              Manage FAQs
            </Button>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-black hover:bg-gray-800 text-white flex items-center gap-2"
            >
              <Plus size={16} />
              Add Package
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center justify-between">
            <span>{success}</span>
            <button onClick={() => setSuccess('')} className="text-green-700 hover:text-green-900">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="text-red-600 hover:text-red-900">
              <X size={16} />
            </button>
          </div>
        )}

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <Input
              type="text"
              placeholder="Search packages by name or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="mt-4 text-gray-600">Loading packages...</p>
          </div>
        ) : filteredPackages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              {searchTerm ? 'No packages found matching your search.' : 'No packages created yet. Click "Add Package" to create one.'}
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredPackages.map((pkg) => (
              <Card key={pkg.id} className={`${!pkg.is_active ? 'opacity-60' : ''} hover:shadow-lg transition-shadow`}>
                <CardHeader>
                  {pkg.package_image_url ? (
                    <div className="w-full h-48 mb-4 rounded-lg overflow-hidden bg-gray-200">
                      <img
                        src={pkg.package_image_url}
                        alt={pkg.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-48 mb-4 rounded-lg bg-gray-100 flex items-center justify-center">
                      <PackageIcon className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{pkg.name}</CardTitle>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      {!pkg.is_active && (
                        <span className="px-2 py-1 text-xs bg-gray-200 text-gray-600 rounded">
                          Inactive
                        </span>
                      )}
                      {pkg.vehicles?.length > 0 && (
                        <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded flex items-center gap-1">
                          <Car size={12} />
                          {pkg.vehicles.length}
                        </span>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">{pkg.description}</p>
                  
                  <div className="mb-4 text-xl font-bold text-gray-900">
                    ₹{pkg.complete_package_price?.toLocaleString() || 0}
                  </div>

                  <div className="flex gap-2 pt-4 border-t border-gray-200">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleActive(pkg)}
                      className="flex-1 text-xs"
                    >
                      {pkg.is_active ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleOpenDialog(pkg)}
                    >
                      <Edit2 size={14} />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteClick(pkg)}
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Package Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPackage ? 'Edit Package' : 'Add New Package'}</DialogTitle>
          </DialogHeader>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              onClick={() => setActiveTab('package-info')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'package-info'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Package Info
            </button>
            <button
              onClick={() => setActiveTab('vehicles')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'vehicles'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Car size={16} className="inline mr-1" />
              Vehicles ({formData.vehicles.length})
            </button>
            <button
              onClick={() => setActiveTab('brochure')}
              className={`px-4 py-2 text-sm font-medium ${
                activeTab === 'brochure'
                  ? 'border-b-2 border-black text-black'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <FileText size={16} className="inline mr-1" />
              Brochure
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Package Info Tab */}
            {activeTab === 'package-info' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Package Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Urban Collection"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe this luxury package..."
                    required
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Complete Package Price (₹) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="1"
                    value={formData.complete_package_price}
                    onChange={(e) => setFormData({ ...formData, complete_package_price: parseInt(e.target.value) || 0 })}
                    placeholder="e.g., 10000000"
                    required
                  />
                  <p className="text-xs text-gray-500">This is the total price for the 360-day package</p>
                </div>

                <div className="space-y-2">
                  <Label>Package Image</Label>
                  {formData.package_image_url ? (
                    <div className="relative">
                      <img
                        src={formData.package_image_url}
                        alt="Package"
                        className="w-full h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={handleRemovePackageImage}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePackageImageUpload}
                        className="hidden"
                        id="package-image-upload"
                        disabled={uploadingPackageImage}
                      />
                      <label
                        htmlFor="package-image-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <Upload className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {uploadingPackageImage ? 'Uploading...' : 'Click to upload package image'}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">JPG, PNG, WEBP</span>
                      </label>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">Active (visible to users)</Label>
                </div>
              </div>
            )}

            {/* Vehicles Tab */}
            {activeTab === 'vehicles' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Vehicles in Package</Label>
                  <Button
                    type="button"
                    onClick={() => handleOpenVehicleDialog()}
                    variant="outline"
                    size="sm"
                  >
                    <Plus size={16} className="mr-1" />
                    Add Vehicle
                  </Button>
                </div>

                {formData.vehicles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                    No vehicles added yet. Click "Add Vehicle" to start.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {formData.vehicles.map((vehicle, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">{vehicle.name}</h4>
                              <span className="px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded">
                                {vehicle.segment}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2 line-clamp-2">{vehicle.description}</p>
                            <div className="flex gap-4 text-xs text-gray-600 mb-2">
                              <span>Usage: {vehicle.usage_days} days/year</span>
                              <span>Shared owners: {vehicle.shared_owners}</span>
                            </div>
                            {vehicle.specifications?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mb-2">
                                {vehicle.specifications.slice(0, 3).map((spec, idx) => (
                                  <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                                    {spec}
                                  </span>
                                ))}
                                {vehicle.specifications.length > 3 && (
                                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                    +{vehicle.specifications.length - 3} more
                                  </span>
                                )}
                              </div>
                            )}
                            {vehicle.images?.length > 0 && (
                              <div className="flex gap-2">
                                {vehicle.images.slice(0, 4).map((img, idx) => (
                                  <img
                                    key={idx}
                                    src={img}
                                    alt={`${vehicle.name} ${idx + 1}`}
                                    className="w-16 h-16 object-cover rounded border border-gray-300"
                                  />
                                ))}
                                {vehicle.images.length > 4 && (
                                  <div className="w-16 h-16 bg-gray-100 rounded border border-gray-300 flex items-center justify-center text-xs text-gray-600">
                                    +{vehicle.images.length - 4}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenVehicleDialog(vehicle, index)}
                            >
                              <Edit2 size={14} />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemoveVehicle(index)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Brochure Tab */}
            {activeTab === 'brochure' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Package Brochure (PDF)</Label>
                  
                  {formData.brochure_url ? (
                    <div className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="w-8 h-8 text-blue-600" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">Brochure Uploaded</p>
                            <a
                              href={formData.brochure_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline"
                            >
                              View Brochure
                            </a>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleRemoveBrochure}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handleBrochureUpload}
                        className="hidden"
                        id="brochure-upload"
                        disabled={uploadingBrochure}
                      />
                      <label
                        htmlFor="brochure-upload"
                        className="cursor-pointer flex flex-col items-center"
                      >
                        <FileText className="w-12 h-12 text-gray-400 mb-2" />
                        <span className="text-sm text-gray-600">
                          {uploadingBrochure ? 'Uploading...' : 'Click to upload brochure'}
                        </span>
                        <span className="text-xs text-gray-500 mt-1">PDF only, max 10MB</span>
                      </label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-gray-800 text-white">
                {editingPackage ? 'Update Package' : 'Create Package'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vehicle Dialog */}
      <Dialog open={vehicleDialogOpen} onOpenChange={setVehicleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingVehicle ? 'Edit Vehicle' : 'Add Vehicle'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleVehicleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="vehicle-name">Vehicle Name *</Label>
              <Input
                id="vehicle-name"
                value={vehicleFormData.name}
                onChange={(e) => setVehicleFormData({ ...vehicleFormData, name: e.target.value })}
                placeholder="e.g., BMW X1"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment">Segment *</Label>
              <select
                id="segment"
                value={vehicleFormData.segment}
                onChange={(e) => setVehicleFormData({ ...vehicleFormData, segment: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
                required
              >
                <option value="Commuter">Commuter</option>
                <option value="Outstation">Outstation</option>
                <option value="Weekender">Weekender</option>
              </select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="usage-days">Usage Days (per year) *</Label>
                <Input
                  id="usage-days"
                  type="number"
                  value={vehicleFormData.usage_days}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, usage_days: parseInt(e.target.value) || 0 })}
                  placeholder="e.g., 180"
                  required
                  min="0"
                  max="365"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shared-owners">Number of Shared Owners *</Label>
                <Input
                  id="shared-owners"
                  type="number"
                  value={vehicleFormData.shared_owners}
                  onChange={(e) => setVehicleFormData({ ...vehicleFormData, shared_owners: parseInt(e.target.value) || 0 })}
                  placeholder="e.g., 2"
                  required
                  min="1"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="vehicle-description">Description *</Label>
              <textarea
                id="vehicle-description"
                value={vehicleFormData.description}
                onChange={(e) => setVehicleFormData({ ...vehicleFormData, description: e.target.value })}
                placeholder="Describe this vehicle..."
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>

            <div className="space-y-2">
              <Label>Specifications (Features)</Label>
              <div className="flex gap-2">
                <Input
                  value={vehicleSpecInput}
                  onChange={(e) => setVehicleSpecInput(e.target.value)}
                  placeholder="Add a specification"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddVehicleSpec();
                    }
                  }}
                />
                <Button type="button" onClick={handleAddVehicleSpec} variant="outline">
                  <Plus size={16} />
                </Button>
              </div>
              {vehicleFormData.specifications.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {vehicleFormData.specifications.map((spec, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm"
                    >
                      {spec}
                      <button
                        type="button"
                        onClick={() => handleRemoveVehicleSpec(index)}
                        className="hover:bg-gray-200 rounded-full p-0.5"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Vehicle Images</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleVehicleImageUpload}
                  className="hidden"
                  id="vehicle-image-upload"
                  disabled={uploadingVehicleImages}
                />
                <label
                  htmlFor="vehicle-image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-10 h-10 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    {uploadingVehicleImages ? 'Uploading...' : 'Click to upload vehicle images'}
                  </span>
                  <span className="text-xs text-gray-500 mt-1">Multiple files supported</span>
                </label>
              </div>

              {vehicleFormData.images.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mt-2">
                  {vehicleFormData.images.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={imageUrl}
                        alt={`Vehicle ${index + 1}`}
                        className="w-full h-24 object-cover rounded border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveVehicleImage(imageUrl)}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
              <Button type="button" variant="outline" onClick={handleCloseVehicleDialog}>
                Cancel
              </Button>
              <Button type="submit" className="bg-black hover:bg-gray-800 text-white">
                {editingVehicle ? 'Update Vehicle' : 'Add Vehicle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* FAQ Management Dialog */}
      <Dialog open={isFAQDialogOpen} onOpenChange={setIsFAQDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage FAQs (Common for All Packages)</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Add New FAQ</Label>
              <Input
                value={faqInput.question}
                onChange={(e) => setFaqInput({ ...faqInput, question: e.target.value })}
                placeholder="FAQ Question"
                className="mb-2"
              />
              <textarea
                value={faqInput.answer}
                onChange={(e) => setFaqInput({ ...faqInput, answer: e.target.value })}
                placeholder="FAQ Answer"
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
              <Button type="button" onClick={handleAddFAQ} variant="outline" className="w-full">
                <Plus size={16} className="mr-2" />
                Add FAQ
              </Button>
            </div>

            <div className="space-y-2">
              <Label>Current FAQs ({faqs.length})</Label>
              {faqs.length === 0 ? (
                <div className="text-center py-8 text-gray-500 border border-dashed border-gray-300 rounded-lg">
                  No FAQs added yet.
                </div>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {faqs.map((faq, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 mb-1">{faq.question}</p>
                          <p className="text-sm text-gray-600">{faq.answer}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveFAQ(index)}
                          className="ml-4 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={() => setIsFAQDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveFAQs} className="bg-black hover:bg-gray-800 text-white">
              Save FAQs
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Package</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete <strong>{packageToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-red-600 mt-2">
              This action cannot be undone. All images and brochures will be permanently deleted from Cloudinary.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setPackageToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete Package
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sequence Manager */}
      <SequenceManager
        items={packages}
        isOpen={isSequenceDialogOpen}
        onClose={() => setIsSequenceDialogOpen(false)}
        onSave={handleUpdateSequences}
        itemDisplayKey="name"
        title="Manage Package Display Sequence"
      />
    </div>
  );
};

export default CurationPage;
